import { FC, useState, useCallback, MouseEvent } from 'react'
import { Typography, IconButton } from '@material-tailwind/react'
import truncate from 'lodash/truncate'
import { PiCheckCircleDuotone, PiPlusBold } from 'react-icons/pi'
import { upsertAgamiExperiences } from '../utils/helpers/upload.helpers'
import { useGeneralToast } from '../hooks/toast'
import { useSessionContext } from '../contexts/SessionContext'
import AgamiTypeChip from '../components/AgamiTypeChip'
import { removeManualCodeLibrarySuffix } from '../utils/helpers/agami.helpers'
import { formatDateString } from '../utils/helpers/dates.helpers'

interface ExperienceContentSpot {
  experience_id: string
  manual_code?: string
}

interface AddAgamiToExpRowProps {
  id: string
  name: string
  description: string
  type: number
  updated_at: string
  experience_content_spots?: ExperienceContentSpot[]
  currentExpId: string
  refetch: () => Promise<void>
}

export const AddAgamiToExpRow: FC<AddAgamiToExpRowProps> = ({
  id,
  name,
  description,
  type,
  updated_at,
  experience_content_spots,
  currentExpId,
  refetch,
}) => {
  const { error: toastError } = useGeneralToast()
  const { user } = useSessionContext()
  const [adding, setAdding] = useState(false)
  const agamiExpRelationIds = experience_content_spots?.map((expRel) => expRel.experience_id) ?? []
  const manualCode = experience_content_spots?.[0]?.manual_code
    ? removeManualCodeLibrarySuffix(experience_content_spots[0].manual_code)
    : undefined

  const addAgamiToExp = useCallback(
    async (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setAdding(true)

      const newExperiences = agamiExpRelationIds.concat([currentExpId])

      const { error } = await upsertAgamiExperiences({
        spot_id: id,
        type,
        manual_code: manualCode,
        newExperiences,
        userDefaultExpId: user?.defaultExperienceId,
      })

      if (error) {
        toastError((error as { message?: string })?.message ?? JSON.stringify(error))
        setAdding(false)
        return
      }

      await refetch()
      setAdding(false)
    },
    [agamiExpRelationIds, currentExpId, id, type, manualCode, user?.defaultExperienceId, toastError, refetch]
  )

  const alreadyPresent = agamiExpRelationIds?.includes(currentExpId)

  return (
    <tr
      key={id}
      className="even:bg-blue-gray-50/50 dark:even:bg-primary-500 text-center"
      data-testclass="add-agami-to-exp-table-row"
    >
      <td className="py-2">
        <Typography
          title={name}
          variant="small"
          className="font-black"
          data-testclass="add-agami-to-exp-table-row-name"
        >
          {truncate(name, {
            separator: ' ',
          })}
        </Typography>
      </td>
      <td className="py-2">
        <Typography title={description} variant="small">
          {truncate(description, {
            separator: ' ',
          })}
        </Typography>
      </td>
      <td className="py-2">
        <Typography title={`Agami type  ${type}`} variant="small">
          <AgamiTypeChip agamiType={type} />
        </Typography>
      </td>
      <td className="py-2">
        <Typography variant="small">{formatDateString(updated_at)}</Typography>
      </td>
      <td className="py-2">
        {alreadyPresent ? (
          <span>
            <PiCheckCircleDuotone className="mx-auto text-green-800 dark:text-green-500 text-[20px] my-1" />
          </span>
        ) : (
          <IconButton
            variant="text"
            size="sm"
            color="yellow"
            onClick={(e) => addAgamiToExp(e)}
            disabled={adding}
            data-testclass="add-agami-to-exp-table-row-btn"
          >
            <PiPlusBold className="text-primary dark:text-base-gray-500 text-[14px]" />
          </IconButton>
        )}
      </td>
    </tr>
  )
}
